import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Check, ChevronRight, FileSpreadsheet, Trash2, Upload, X, XCircle } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import TelaProcessando from '@/components/TelaProcessando';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { usePode } from '@/stores/authStore';
import DadosDoRelatorio from './DadosDoRelatorio';
import PassosDaFolha from './PassosDaFolha';
import { useConferencia, useFolha } from './useFolha';
import { erroDaApi, reais, type EnvioDeRelatorios, type MudancaDeLancamento, type RelatorioRecebido } from './tipos';

type Origem = number | 'totalizador';

/** Quantas mudanças aparecem antes do "e mais". */
const MUDANCAS_VISIVEIS = 8;

/**
 * O efeito do envio ou da remoção nos lançamentos do mês: é o que responde "e
 * aí, mudou alguma coisa?".
 */
function MudancasNosLancamentos({ mudancas }: { mudancas: MudancaDeLancamento[] }) {
  if (mudancas.length === 0) {
    return (
      <p className="mt-sm border-t border-border pt-sm text-[13px] text-muted">
        Nenhum lançamento mudou de valor: os impostos que valiam já eram os mesmos.
      </p>
    );
  }

  return (
    <div className="mt-sm border-t border-border pt-sm text-[13px]">
      <p className="font-bold text-ink">
        {mudancas.length === 1 ? '1 lançamento mudou de valor:' : `${mudancas.length} lançamentos mudaram de valor:`}
      </p>
      <ul className="mt-xs space-y-[2px]">
        {mudancas.slice(0, MUDANCAS_VISIVEIS).map((mudanca) => (
          <li key={mudanca.historico} className="flex flex-wrap justify-between gap-x-md">
            <span className="text-ink">{mudanca.historico}</span>
            <span className="tabular-nums">
              <span className="text-muted">{mudanca.antes === null ? 'nova' : reais.format(mudanca.antes)}</span>
              {' → '}
              <span className="font-bold text-ink">{mudanca.depois === null ? 'removida' : reais.format(mudanca.depois)}</span>
            </span>
          </li>
        ))}
      </ul>
      {mudancas.length > MUDANCAS_VISIVEIS && (
        <p className="mt-xs text-muted">e mais {mudancas.length - MUDANCAS_VISIVEIS}. Veja todos na planilha para o ERP.</p>
      )}
    </div>
  );
}

/** "2026-09-16T14:32:00-03:00" vira "16/09 às 14:32". */
function quando(iso: string): string {
  const data = new Date(iso);
  const dia = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return `${dia} às ${hora}`;
}

/**
 * Os relatórios de imposto, que chegam dias depois da folha: "eu recebo essa
 * folha, muitas vezes eu não tenho o INSS ainda".
 *
 * A mesma tela serve aos dois passos — um relatório por setor e o totalizador
 * da empresa —, porque o envio é igual: o próprio arquivo diz de quem é. O
 * relatório não diz de que mês é; o que destoa da folha volta com aviso.
 */
export default function ImpostosPage() {
  const { folhaId, data: folha, isLoading } = useFolha();
  const { data: conferencia } = useConferencia(folhaId, Boolean(folha));
  const podeImportar = usePode('folha.importar');
  const queryClient = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [abertos, setAbertos] = useState<Set<Origem>>(new Set());
  const [removendo, setRemovendo] = useState<{ origem: Origem; nome: string } | null>(null);
  // Competência já enviada ao ERP: os arquivos esperam a confirmação.
  const [aConfirmar, setAConfirmar] = useState<File[] | null>(null);
  // A área de envio só aparece quando pedida: no dia a dia a tela é para consultar.
  const [painelAberto, setPainelAberto] = useState(false);
  const ehTotalizador = useLocation().pathname.endsWith('/totalizador');

  // O que depende dos impostos: a competência, a conferência, os lançamentos e o card da lista.
  const recarregar = () => {
    void queryClient.invalidateQueries({ queryKey: ['folha', folhaId] });
    void queryClient.invalidateQueries({ queryKey: ['folha-conferencia', folhaId] });
    void queryClient.invalidateQueries({ queryKey: ['folha-lancamentos', folhaId] });
    void queryClient.invalidateQueries({ queryKey: ['folhas'] });
  };

  const enviar = useMutation({
    mutationFn: async (arquivos: FileList | File[]) => {
      const corpo = new FormData();
      Array.from(arquivos).forEach((arquivo) => corpo.append('arquivos[]', arquivo));

      return {
        ...(await api.post<{ data: EnvioDeRelatorios }>(`/folhas/${folhaId}/tributos`, corpo)).data.data,
        em: new Date().toISOString(),
      };
    },
    onSuccess: recarregar,
  });

  const remover = useMutation({
    mutationFn: async ({ origem, nome }: { origem: Origem; nome: string }) => ({
      nome,
      em: new Date().toISOString(),
      ...(await api.delete<{ data: { mudancas: MudancaDeLancamento[] } }>(`/folhas/${folhaId}/relatorios/${origem}`)).data.data,
    }),
    onSuccess: (_, { origem }) => {
      enviar.reset();
      setRemovendo(null);
      setAbertos((atual) => {
        const proximo = new Set(atual);
        proximo.delete(origem);

        return proximo;
      });
      recarregar();
    },
  });

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (!folha || !folhaId) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível abrir esta competência.</p>;
  }

  if (enviar.isPending) {
    return <TelaProcessando titulo="Lendo os relatórios" detalhe="Conferindo cada arquivo e refazendo os lançamentos do mês." />;
  }

  if (remover.isPending) {
    return <TelaProcessando titulo="Removendo o relatório" detalhe="O setor volta aos valores do PDF e os lançamentos são refeitos." />;
  }

  const comRelatorio = folha.setores.filter((setor) => setor.relatorio_recebido);
  const enviada = folha.status === 'exportada';

  // Na competência já enviada ao ERP, mexer nos impostos refaz o lote: pergunta antes.
  const receber = (arquivos: FileList | File[]) => {
    const lista = Array.from(arquivos);
    // Fecha a área de envio: o que importa agora é o resultado.
    setPainelAberto(false);

    if (enviada) {
      setAConfirmar(lista);
    } else {
      remover.reset();
      enviar.mutate(lista);
    }
  };

  const alternar = (origem: Origem) =>
    setAbertos((atual) => {
      const proximo = new Set(atual);

      if (!proximo.delete(origem)) {
        proximo.add(origem);
      }

      return proximo;
    });

  const linha = (origem: Origem, nome: string, relatorio: RelatorioRecebido | null) => {
    const aberto = abertos.has(origem);

    return (
      <li key={String(origem)} className="px-md py-sm text-[13px]">
        <div className="flex flex-wrap items-center justify-between gap-sm">
          <span className="font-bold text-ink">{nome}</span>

          {relatorio ? (
            <span className="flex min-w-0 flex-wrap items-center justify-end gap-sm">
              <span className="flex min-w-0 items-center gap-xs text-muted">
                <span className="font-bold text-success">pelo escritório</span>·
                <span title={relatorio.arquivo ?? undefined} className="max-w-[280px] truncate">
                  {relatorio.arquivo ?? 'arquivo sem nome'}
                </span>
                · <span className="whitespace-nowrap">{quando(relatorio.recebido_em)}</span>
              </span>
              <button
                type="button"
                aria-expanded={aberto}
                onClick={() => alternar(origem)}
                className="inline-flex items-center gap-xs rounded-md px-sm py-[3px] font-bold text-accent hover:bg-accent/10"
              >
                <ChevronRight size={14} className={`transition-transform ${aberto ? 'rotate-90' : ''}`} />
                {aberto ? 'Fechar dados' : 'Ver dados'}
              </button>
              {podeImportar && (
                <button
                  type="button"
                  onClick={() => {
                    remover.reset();
                    setRemovendo({ origem, nome });
                  }}
                  title="Remover o relatório: valem de novo os valores do PDF da folha"
                  className="rounded-md p-[5px] text-muted hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </span>
          ) : (
            <span className="text-muted">{origem === 'totalizador' ? 'ainda não recebido' : 'pelo PDF da folha'}</span>
          )}
        </div>

        {relatorio && aberto && <DadosDoRelatorio folhaId={folhaId} origem={origem} />}
      </li>
    );
  };

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="text-[22px] font-bold text-ink">
            {ehTotalizador ? 'Totalizador da empresa' : 'Impostos por setor'}
          </h1>
          <p className="mt-xs text-[13px] text-muted">
            {ehTotalizador
              ? 'O resumo que fecha o mês inteiro. É dele que saem o pró-labore e os autônomos, que a folha não traz.'
              : 'Um relatório por setor. Enquanto não chegam, valem os valores do próprio PDF da folha.'}
          </p>
        </div>

        {podeImportar && (
          <button
            type="button"
            aria-expanded={painelAberto}
            onClick={() => setPainelAberto((aberto) => !aberto)}
            className={`flex h-10 items-center gap-sm rounded-lg px-md text-[14px] font-bold ${
              painelAberto ? 'border border-border text-ink hover:bg-surface-subtle' : 'bg-accent text-white hover:bg-accent/90'
            }`}
          >
            {painelAberto ? <X size={16} /> : <Upload size={16} />}
            {painelAberto ? 'Fechar envio' : ehTotalizador ? 'Enviar totalizador' : 'Enviar relatórios'}
          </button>
        )}
      </header>

      <PassosDaFolha folha={folha} conferencia={conferencia} atual={ehTotalizador ? 'totalizador' : 'impostos'} />

      {painelAberto && podeImportar && (
        <div
          onDragOver={(evento) => {
            evento.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(evento) => {
            evento.preventDefault();
            setArrastando(false);
            if (evento.dataTransfer.files.length) {
              receber(evento.dataTransfer.files);
            }
          }}
          className={`rounded-lg border-2 border-dashed px-lg py-xl text-center transition-colors ${
            arrastando ? 'border-accent bg-accent/5' : 'border-border bg-surface'
          }`}
        >
          <FileSpreadsheet size={26} className="mx-auto text-muted" />
          <p className="mt-md text-[15px] font-bold text-ink">
            Arraste aqui {ehTotalizador ? 'o totalizador' : 'os relatórios do escritório'}
          </p>
          <p className="mt-xs text-[13px] text-muted">
            planilhas .xls ou .xlsx · pode soltar vários de uma vez · mandar de novo substitui o anterior
          </p>
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="mt-sm inline-flex h-9 items-center gap-sm rounded-lg border border-border px-md text-[13px] font-bold text-ink hover:bg-surface-subtle"
          >
            <Upload size={15} />
            Escolher arquivos
          </button>
          <input
            ref={entrada}
            type="file"
            accept=".xls,.xlsx"
            multiple
            className="hidden"
            onChange={(evento) => {
              if (evento.target.files?.length) {
                // Copia antes de limpar o campo, que troca a lista de arquivos.
                receber(Array.from(evento.target.files));
              }
              // Deixa escolher o mesmo arquivo de novo.
              evento.target.value = '';
            }}
          />
        </div>
      )}

      {enviar.isError && (
        <p className="mt-md flex items-center gap-sm rounded-lg bg-danger/10 px-md py-sm text-[13px] font-bold text-danger">
          <XCircle size={16} />
          {erroDaApi(enviar.error, 'Não foi possível enviar os arquivos. Tente de novo.')}
        </p>
      )}

      {enviar.data && (
        <section className="mt-md rounded-lg border border-border bg-surface p-md">
          <div className="flex items-start justify-between gap-sm">
            <h2 className="text-[14px] font-bold text-ink">
              Envio de {quando(enviar.data.em)}: {enviar.data.recebidos.length}{' '}
              {enviar.data.recebidos.length === 1 ? 'arquivo aproveitado' : 'arquivos aproveitados'}
              {enviar.data.recusados.length > 0 && `, ${enviar.data.recusados.length} recusado${enviar.data.recusados.length === 1 ? '' : 's'}`}
            </h2>
            <button type="button" onClick={() => enviar.reset()} title="Fechar" className="rounded-md p-[4px] text-muted hover:bg-surface-subtle">
              <X size={15} />
            </button>
          </div>

          <ul className="mt-sm space-y-sm text-[13px]">
            {enviar.data.recebidos.map((recebido) => {
              const destino = recebido.tipo === 'totalizador' ? 'totalizador da empresa' : recebido.setor;
              const origem: Origem = recebido.tipo === 'totalizador' ? 'totalizador' : (recebido.setor_id ?? 0);
              const comAviso = recebido.avisos.length > 0;

              return (
                <li key={recebido.arquivo} className="flex gap-sm">
                  {comAviso ? (
                    <AlertTriangle size={16} className="mt-[1px] shrink-0 text-warning" />
                  ) : (
                    <Check size={16} className="mt-[1px] shrink-0 text-success" />
                  )}
                  <div>
                    <p className="text-ink">
                      <span className="font-bold">{recebido.arquivo}</span> → {destino}
                      <span className="text-muted">
                        {recebido.substituiu
                          ? ` · substituiu o relatório de ${quando(recebido.substituiu)}`
                          : recebido.tipo === 'totalizador'
                            ? ' · primeiro totalizador desta competência'
                            : ' · primeiro relatório deste setor'}
                      </span>
                    </p>
                    {comAviso && (
                      <div className="mt-[2px] text-warning">
                        {recebido.avisos.map((aviso) => (
                          <p key={aviso}>{aviso}</p>
                        ))}
                        <p className="flex flex-wrap items-center gap-sm">
                          Confira se o arquivo é do mês certo.
                          {podeImportar && (
                            <button
                              type="button"
                              onClick={() => {
                                remover.reset();
                                setRemovendo({ origem, nome: destino ?? recebido.arquivo });
                              }}
                              className="font-bold text-danger hover:underline"
                            >
                              Remover este relatório
                            </button>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}

            {enviar.data.recusados.map((recusado) => (
              <li key={recusado.arquivo} className="flex gap-sm">
                <XCircle size={16} className="mt-[1px] shrink-0 text-danger" />
                <p className="text-ink">
                  <span className="font-bold">{recusado.arquivo}</span> <span className="text-danger">não foi aproveitado:</span>{' '}
                  {recusado.motivo}
                </p>
              </li>
            ))}
          </ul>

          {enviar.data.recebidos.length > 0 && <MudancasNosLancamentos mudancas={enviar.data.mudancas} />}
        </section>
      )}

      {remover.data && (
        <section className="mt-md rounded-lg border border-border bg-surface p-md">
          <div className="flex items-start justify-between gap-sm">
            <h2 className="flex items-center gap-sm text-[14px] font-bold text-ink">
              <Check size={16} className="text-success" />
              Relatório de {remover.data.nome} removido em {quando(remover.data.em)}: valem de novo os valores do PDF da folha.
            </h2>
            <button type="button" onClick={() => remover.reset()} title="Fechar" className="rounded-md p-[4px] text-muted hover:bg-surface-subtle">
              <X size={15} />
            </button>
          </div>
          <MudancasNosLancamentos mudancas={remover.data.mudancas} />
        </section>
      )}

      {ehTotalizador && (
        <section className="mt-lg overflow-hidden rounded-lg border border-border bg-surface">
          <h2 className="border-b border-border px-md py-sm text-[14px] font-bold text-ink">
            {folha.totalizador_recebido ? 'Totalizador recebido' : 'Totalizador ainda não recebido'}
          </h2>
          <ul>{linha('totalizador', 'Totalizador da empresa', folha.totalizador)}</ul>
        </section>
      )}

      <section className="mt-lg overflow-hidden rounded-lg border border-border bg-surface">
        <h2 className="border-b border-border px-md py-sm text-[14px] font-bold text-ink">
          Relatórios dos setores recebidos: {comRelatorio.length} de {folha.setores.length}
        </h2>
        <ul className="divide-y divide-border">{folha.setores.map((setor) => linha(setor.id, setor.nome, setor.relatorio))}</ul>
      </section>

      <Dialog open={removendo !== null} onOpenChange={(aberto) => !remover.isPending && !aberto && setRemovendo(null)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>Remover o relatório de {removendo?.nome}?</DialogTitle>
          <DialogDescription className="mt-xs">
            {removendo?.origem === 'totalizador'
              ? 'As linhas do pró-labore e dos autônomos ficam sem os valores do totalizador até chegar outro.'
              : 'O setor volta aos valores do próprio PDF da folha.'}{' '}
            Os lançamentos do mês são refeitos. Para usar outro arquivo, basta enviá-lo de novo.
          </DialogDescription>
          {enviada && <AvisoEnviada />}
          {remover.isError && (
            <p className="mt-sm text-[13px] font-bold text-danger">{erroDaApi(remover.error, 'Não foi possível remover o relatório.')}</p>
          )}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setRemovendo(null)}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => removendo && remover.mutate(removendo)}
              className="inline-flex items-center gap-xs rounded-md bg-danger px-md py-sm text-[13px] font-bold text-white hover:bg-danger/90"
            >
              <Trash2 size={14} aria-hidden="true" />
              Remover relatório
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={aConfirmar !== null} onOpenChange={(aberto) => !aberto && setAConfirmar(null)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>
            Enviar {aConfirmar?.length === 1 ? 'o relatório' : `${aConfirmar?.length} relatórios`} para uma competência já enviada?
          </DialogTitle>
          <DialogDescription className="mt-xs">{aConfirmar?.map((arquivo) => arquivo.name).join(', ')}</DialogDescription>
          <AvisoEnviada />
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setAConfirmar(null)}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                if (aConfirmar) {
                  remover.reset();
                  enviar.mutate(aConfirmar);
                }
                setAConfirmar(null);
              }}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90"
            >
              <Upload size={14} aria-hidden="true" />
              Enviar mesmo assim
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** O lote desta competência já está no Compumate: mudar aqui não muda lá. */
function AvisoEnviada() {
  return (
    <p className="mt-sm flex gap-xs rounded-md bg-warning/10 p-sm text-[13px] text-ink">
      <AlertTriangle size={16} className="mt-[1px] shrink-0 text-warning" />
      Esta competência já foi enviada ao ERP. Os lançamentos vão ser refeitos e ela volta para conferência: se algum valor
      mudar, será preciso baixar a planilha e importar no Compumate de novo.
    </p>
  );
}
